/**
 * InfraInventoryReview — V105-3-6, the infrastructure inventory review in a FedRAMP / GovRAMP
 * program's Scope tab (`?view=inventory`), next to "Resources inside those services" (the
 * connected-cloud suggestions) (ADR-0072 §1; PRD G-2: each item lands as a suggestion a person
 * accepts). Three-critic fixes (aesthetic + accessibility + flow), rounds 1 and 2, 2026-10-08.
 *
 *   - One row per service the product depends on (provider × product). While the federal-data
 *     answer (which decides the verdict) is incomplete, the row asks it with one compact select
 *     that saves on commit (Q1, then Q2 when Q1 = no); otherwise a chip after the name says it.
 *     The full answer form lives in "Answer them" and behind "Where it was found".
 *   - Decide per row: "Add to <program> boundary" (service, component and this program's boundary
 *     in one server transaction), "Add to services only", "Link…" (the workspace-wide list with a
 *     filter), Dismiss (a reason). A row whose service is already registered offers Link first.
 *     Bulk: high-confidence Add / low-confidence Dismiss in the header, or tick rows. Every
 *     decision raises one Toast with Undo; focus moves to the next row.
 *   - Undo that would leave a component in a boundary a person put it in asks once, inline.
 *   - Done means facts: nothing to review AND every kept service has its federal-data answer.
 *   - Unknown outbound domains: per domain "This is us", "It's a service…", "Ignore…", with Undo.
 *   - Readers see the list and one ask-an-admin line, no buttons.
 *
 * Reads (any member): items (+ review facts), services, intake, unmatched egress, connections, scans,
 * boundary components. Writes (owner / admin or the lead of a FedRAMP / GovRAMP program; `can_write`
 * from the server; adding into a boundary also needs that program's lead, which `scope` reflects):
 * decisions, scans, uploads, intake answers, Q1 / Q2, domain decisions, boundary scope.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Checkbox, EmptyState, Field, Input, Select, Spinner, Textarea, Toast } from '@aigovops/design-system';
import { ApiError, listConnectors } from '../../lib/api';
import {
  acceptInventory, answerItem, confirmAnswer, dismissInventory, elicitIntake, getIntake, getItemAnswers, getScan, linkInventory, listEgress, listInventory,
  listLinkableServices, listScans, reopenInventory, resolveEgress, setInventoryFunction, startScan, submitIntake, suggestItem, syncInventory, undoEgress, uploadEgress,
  type EgressSourceKey, type ElicitProposal, type InventoryRowItem, type ScanJob, type ScanStartOutcome,
} from '../../lib/infra-plan-api';
import { addToScope, createComponent, listComponents, removeFromScope } from '../../lib/boundary-api';
import {
  bulkCandidates, confidenceLabel, federalChip, federalPending, fileSizeLabel, filterByName, inventoryProgress, itemFunction, needsFederalAnswer, nextFocusId, productChoices,
} from '../../lib/infra-inventory-model';
import { programLabel } from '../../lib/boundary-model';
import { fmtDate } from '../../lib/format';
import { DisclosureMenu, SplitButton } from './DisclosureMenu';
import { ReconnectGitHub, githubReturnMessage } from '../ReconnectGitHub';
import { AskAdmin } from '../AskAdmin';
import type { ToastApi } from './program-toast';
import './infra-inventory.css';

export { confidenceLabel } from '../../lib/infra-inventory-model';

interface ToastState { message: string; details?: string; undo?: () => Promise<void> | void; version: number }
interface Problem { message: string; action?: { label: string; run: () => void } }
type Service = { external_service_id: string; name: string };
type Blocked = { item: InventoryRowItem; programs: string[] };

const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);
const body = (e: unknown) => (e instanceof ApiError ? (e.body as Record<string, unknown> | undefined) : undefined);
const INVENTORY_SCOPE_REASON = 'Added from the infrastructure inventory';

export const SOURCE_LABEL: Record<string, string> = {
  repo_scan: 'Code', connector_resource: 'Cloud', sbom_component: 'SBOM', oauth_app: 'Sign-in apps', egress: 'Network', intake: 'You said',
};
export const FUNCTION_LABEL: Record<string, string> = {
  primary_data_store: 'Primary data store', secondary_data_store: 'Secondary data store', compute: 'Compute', inference: 'AI inference',
  identity: 'Identity', messaging: 'Messaging', observability: 'Logs and monitoring', ci_cd: 'Build and deploy', edge_network: 'Edge network',
  business_support: 'Business tool', other: 'Other',
};
const fnLabel = (f: string | null | undefined) => (f ? FUNCTION_LABEL[f] ?? 'Other' : 'Not set');
const Q1_LABEL: Record<string, string> = { yes: 'Yes, it holds federal data', metadata_only: 'Only account or service metadata', no: 'No' };
const Q2_LABEL: Record<string, string> = { yes: 'Yes, it could affect that data', no: 'No' };
const DISMISS_REASONS = ['Not used by the product', 'A customer system we only read', 'Retired'];
const EGRESS_SOURCES: Array<{ value: EgressSourceKey; label: string }> = [
  { value: 'cloudflare_logpush', label: 'Cloudflare Logpush (Gateway DNS or HTTP)' },
  { value: 'vpc_flow', label: 'DNS query logs (AWS Route 53 Resolver, Google Cloud DNS)' },
  { value: 'customer_collector', label: 'GAC Pro collector export' },
];
const MAX_UPLOAD = 5 * 1024 * 1024;
const keyOf = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 150) || 'service';

type Panel = null | 'scan' | 'intake' | 'egress' | 'domains';

/** Scan polling backoff (ms): quick at first, then every 10 s until the job is done or failed. */
export const SCAN_POLL_MS = [1000, 2000, 3000, 5000, 8000, 10000] as const;
const scanActive = (j: ScanJob | null | undefined): j is ScanJob => !!j && (j.status === 'queued' || j.status === 'running');
/** "Scanning <repo> · n files read", plus a note when the server says no files were read for a minute (`stalled`, one definition with the restart rule). */
const scanLine = (j: ScanJob) =>
  `Scanning ${j.target} · ${j.files_read} file${j.files_read === 1 ? '' : 's'} read${j.stalled ? ' · no progress for a minute' : ''}`;
/** The words for a rejoined scan; with a full scan queued behind a quick one, says when the full scan runs. */
export const rejoinNote = (target: string, queued?: 'full') =>
  queued === 'full' ? `A quick scan is running · the full scan runs next · ${target}` : `Still running · rejoined · ${target}`;
type ScanProblem = { kind: 'failed' | 'lost' | 'gone'; job: ScanJob } | { kind: 'start'; message: string };
/** Failures a new scan can fix; the others need the GitHub connection or an administrator. */
const SCAN_RETRYABLE = new Set(['scan_failed', 'dispatch_failed', 'timed_out']);
const SCAN_CONNECTION = new Set(['no_credential', 'no_repo', 'not_github']);

/** A count announced after typing settles (no announcement per keystroke). */
function useSettled(text: string, ms = 300): string {
  const [v, setV] = useState('');
  useEffect(() => { const t = window.setTimeout(() => setV(text), ms); return () => window.clearTimeout(t); }, [text, ms]);
  return v;
}

/**
 * A select that saves when the choice is committed — a pointer pick, Enter, or leaving it —
 * not on every arrow key (a closed select fires change on arrows on Windows).
 */
function useCommitSelect(initial: string, commit: (v: string) => void) {
  const [value, setValue] = useState(initial);
  const pointer = useRef(false);
  useEffect(() => { setValue(initial); }, [initial]);
  return {
    value,
    props: {
      value,
      onMouseDown: () => { pointer.current = true; },
      onPointerDown: () => { pointer.current = true; },
      onKeyDown: (e: React.KeyboardEvent<HTMLSelectElement>) => { pointer.current = false; if (e.key === 'Enter') { e.preventDefault(); commit(e.currentTarget.value); } },
      onChange: (e: React.ChangeEvent<HTMLSelectElement>) => { const v = e.currentTarget.value; setValue(v); if (pointer.current) { pointer.current = false; commit(v); } },
      onBlur: (e: React.FocusEvent<HTMLSelectElement>) => { pointer.current = false; commit(e.currentTarget.value); },
    },
  };
}

export function InfraInventoryReview({ slug, prefix, program, scope, scopeNote, onBack, onContinue, onOpenVerdicts, toastApi }: {
  slug: string; prefix: string; program: string;
  /** Where "Add to <program> boundary" writes (period null = standing). Null: this program's boundary can't change here. */
  scope?: { period_id: string | null } | null;
  /** Why adds go to services only when `scope` is null ("the period is locked for audit"). */
  scopeNote?: string | null;
  onBack: () => void;
  /** Track 4 hook: the verdicts step, shown once the inventory is complete. */
  onContinue?: () => void;
  /** V105-4-6: "See the verdicts" in the header once any service is added or linked. */
  onOpenVerdicts?: () => void;
  /** V105-4-6 A-H1: the Scope tab's single toast host (else this screen renders its own). */
  toastApi?: ToastApi;
}) {
  const qc = useQueryClient();
  const key = (k: string, ...rest: unknown[]) => ['infra-plan', slug, k, ...rest] as const;
  const items = useQuery({ queryKey: key('items'), queryFn: () => listInventory(slug) });
  const unknown = useQuery({ queryKey: key('egress-unmatched'), queryFn: () => listEgress(slug, true) });
  const services = useQuery({ queryKey: key('services'), queryFn: () => listLinkableServices(slug), staleTime: 30_000 });
  const label = programLabel(program);
  const [tab, setTab] = useState<'open' | 'decided'>('open');
  const [onlyFederal, setOnlyFederal] = useState(false);
  const [panel, setPanel] = useState<Panel>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [rowProblem, setRowProblem] = useState<Record<string, Problem>>({});
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<{ done: number; total: number } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focusTo, setFocusTo] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const panelTrigger = useRef<HTMLElement | null>(null);
  const version = useRef(0);
  const focusedOnLoad = useRef(false);
  // V105-3-6 deferral: scans run on the server; this screen polls them, so closing the panel never stops one.
  // One entry per connection: its running job, or the last one that failed (the panel shows why).
  const scanList = useQuery({ queryKey: key('scans'), queryFn: () => listScans(slug) });
  const [scanJobs, setScanJobs] = useState<Record<string, ScanJob>>({});
  const [scanProblem, setScanProblem] = useState<ScanProblem | null>(null);
  const [scanDone, setScanDone] = useState<string | null>(null);
  const [scanLost, setScanLost] = useState(false);
  const [pollRound, setPollRound] = useState(0);
  const scanEnded = useRef<Set<string>>(new Set());
  // A failed scan named once per browser session (the server keeps listing it for 24 h until the next scan).
  const seenKey = `gacpro.scan-failed-seen:${slug}`;
  const scanFailSeen = useRef<Set<string>>(new Set((() => { try { return JSON.parse(window.sessionStorage.getItem(seenKey) ?? '[]') as string[]; } catch { return []; } })()));
  const markFailSeen = (id: string) => {
    scanFailSeen.current.add(id);
    try { window.sessionStorage.setItem(seenKey, JSON.stringify([...scanFailSeen.current].slice(-50))); } catch { /* private mode: in-memory only */ }
  };
  const [scanPanelFor, setScanPanelFor] = useState<string | null>(null);
  // 2026-10-12: back from a GitHub Reconnect started here (?github=reconnected | failed&connector=…): reopen the scan
  // panel on that connection with the outcome, then clear the params (the rest of the URL, e.g. view=inventory, stays).
  const [sp, setSp] = useSearchParams();
  const [githubReturn, setGithubReturn] = useState(() => githubReturnMessage(sp.get('github'), sp.get('reason'), sp.get('owner'), sp.get('merged')));
  const returnedConnector = useRef(sp.get('connector'));
  useEffect(() => {
    if (!sp.has('github')) return;
    if (githubReturn) { setScanPanelFor(returnedConnector.current); setPanel('scan'); }
    setSp((prev) => { const next = new URLSearchParams(prev); for (const k of ['github', 'connector', 'reason']) next.delete(k); return next; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [scanLive, setScanLive] = useState('');
  const stallAnnounced = useRef<Set<string>>(new Set());
  const liveTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(liveTimer.current), []);
  // Read-only members see scan status but never get actions they can't take (the server would answer 403).
  const canWriteScan = !!items.data?.can_write;
  const undoToastAt = useRef(0);
  const panelNow = useRef<Panel>(null);
  panelNow.current = panel;
  const scanJobsNow = useRef(scanJobs);
  scanJobsNow.current = scanJobs;
  const noteProgress = (_j: ScanJob) => undefined;
  // On open: scans still running are followed again; a scan that failed while nobody watched is named once.
  useEffect(() => {
    const d = scanList.data;
    if (!d) return;
    const running = (d.active ?? []).filter((j) => !scanEnded.current.has(j.job_id));
    if (running.length) {
      setScanJobs((cur) => { const n = { ...cur }; for (const j of running) if (!scanActive(n[j.connector_id])) { n[j.connector_id] = j; noteProgress(j); } return n; });
    }
    const failed = (d.failed ?? []).find((j) => !scanFailSeen.current.has(j.job_id) && !scanEnded.current.has(j.job_id) && !running.some((r) => r.connector_id === j.connector_id));
    if (failed) {
      markFailSeen(failed.job_id);
      setScanJobs((cur) => (cur[failed.connector_id] ? cur : { ...cur, [failed.connector_id]: failed }));
      setScanProblem((cur) => cur ?? { kind: 'failed', job: failed });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanList.data]);
  const activeIds = Object.values(scanJobs).filter(scanActive).map((j) => j.job_id).sort().join(',');
  useEffect(() => {
    if (!activeIds || scanLost) return;
    const ids = activeIds.split(',');
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let n = 0;
    let misses = 0;
    const next = () => { timer = setTimeout(() => void poll(), SCAN_POLL_MS[Math.min(n++, SCAN_POLL_MS.length - 1)]); };
    const poll = async () => {
      let unread = false;
      for (const id of ids) {
        try {
          const r = await getScan(slug, id);
          if (cancelled) return;
          noteProgress(r.scan);
          setScanJobs((cur) => ({ ...cur, [r.scan.connector_id]: r.scan }));
          if (!scanActive(r.scan)) void endScan(r.scan);
        } catch (e) {
          if (cancelled) return;
          if (e instanceof ApiError && e.status === 404) {
            const gone = Object.values(scanJobs).find((j) => j.job_id === id);
            setScanJobs((cur) => { const c = { ...cur }; for (const [k, j] of Object.entries(c)) if (j.job_id === id) delete c[k]; return c; });
            if (gone) setScanProblem({ kind: 'gone', job: gone });
          } else unread = true;
        }
      }
      // Three rounds in a row without reading the status: stop and say so (the line would freeze otherwise).
      if (unread) { if (++misses >= 3) { setScanLost(true); return; } } else misses = 0;
      next();
    };
    next();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIds, slug, scanLost, pollRound]);
  // A stalled scan's note (and its Scan again) is announced once per job.
  useEffect(() => {
    const j = Object.values(scanJobs).find((x) => scanActive(x) && x.stalled && !stallAnnounced.current.has(x.job_id));
    if (!j) return;
    stallAnnounced.current.add(j.job_id);
    announce(`No progress for a minute · ${j.target}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanJobs]);
  useEffect(() => {
    if (!scanLost) return;
    const j = Object.values(scanJobs).find(scanActive);
    if (j) setScanProblem({ kind: 'lost', job: j });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanLost]);

  // The heading takes focus once the review has loaded (the view switch moved it here).
  useEffect(() => {
    if (!items.data || focusedOnLoad.current) return;
    focusedOnLoad.current = true;
    heading.current?.focus();
  }, [items.data]);
  // After a decision: the next row's first action, the row's Q2 select ('q2:<id>'), its "what it does" select ('fn:<id>'), or the heading.
  useEffect(() => {
    if (!focusTo) return;
    const id = window.requestAnimationFrame(() => {
      const [kind, rest] = focusTo.includes(':') ? (focusTo.split(/:(.*)/s) as [string, string]) : ['row', focusTo];
      const root = wrap.current;
      const el = focusTo === 'heading' || !root ? null
        : focusTo === 'scan-submit' ? root.querySelector<HTMLElement>('#inv-panel-scan button[type="submit"]')
        : focusTo === 'scan-heading' ? root.querySelector<HTMLElement>('#inv-panel-scan .gp-card-h')
        : kind === 'q2' ? root.querySelector<HTMLElement>(`[data-item-id="${rest}"] select[data-q="q2"]`)
          : kind === 'fn' ? root.querySelector<HTMLElement>(`[data-item-id="${rest}"] .gp-inv-fn select`)
            : root.querySelector<HTMLElement>(`[data-item-id="${rest}"] .gp-cb-act button:not([aria-disabled="true"])`)
              ?? root.querySelector<HTMLElement>(`[data-item-id="${rest}"] select`);
      (el ?? heading.current)?.focus();
      setFocusTo(null);
    });
    return () => window.cancelAnimationFrame(id);
  }, [focusTo, items.data]);

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['infra-plan', slug] }),
    qc.invalidateQueries({ queryKey: ['boundary', slug] }),
    qc.invalidateQueries({ queryKey: ['program-external-services', slug] }),
  ]);
  const show = (t: Omit<ToastState, 'version'>) => {
    undoToastAt.current = t.undo ? Date.now() : 0;
    if (toastApi) { toastApi.show({ ...t, returnFocus: heading.current }); return; }
    version.current += 1; setToast({ ...t, version: version.current });
  };
  const hideToast = () => { undoToastAt.current = 0; if (toastApi) toastApi.hide(); else setToast(null); };
  /**
   * A scan ended: announced once. It never replaces an Undo toast raised in the last minute (the
   * person may be about to press it): then the end shows as a line on the page instead. Focus moves
   * to the heading only when it was inside the scan panel (which closes); otherwise it stays put.
   */
  const endScan = async (job: ScanJob) => {
    if (scanEnded.current.has(job.job_id)) return;
    scanEnded.current.add(job.job_id);
    clearRejoinNote(job.target);
    // The quick scan ended: the server starts the queued full scan now; re-read the list so it is followed.
    const fullQueued = fullNext.current.delete(job.target.toLowerCase());
    if (fullQueued) void qc.invalidateQueries({ queryKey: key('scans') });
    if (job.status === 'done') {
      await refresh();
      const focusInPanel = !!document.getElementById('inv-panel-scan')?.contains(document.activeElement);
      const othersRunning = Object.values(scanJobsNow.current).some((j) => j.connector_id !== job.connector_id && scanActive(j));
      setScanJobs((cur) => { const c = { ...cur }; delete c[job.connector_id]; return c; });
      setScanProblem((cur) => (cur && 'job' in cur && cur.job.connector_id === job.connector_id ? null : cur));
      if (panelNow.current === 'scan' && !othersRunning) setPanel(null);
      const n = job.items_found ?? 0;
      const message = `Scan finished · ${n} service${n === 1 ? '' : 's'} found${fullQueued ? ' · the full scan starts now' : ''}`;
      if (Date.now() - undoToastAt.current < 60_000) { setScanDone(`${message} in ${job.target}`); announce(`${message} in ${job.target}`); }
      else show({ message, details: `${job.target}: ${n} service${n === 1 ? '' : 's'} found.` });
      if (focusInPanel && !othersRunning) setFocusTo('heading');
      return;
    }
    markFailSeen(job.job_id);
    void qc.invalidateQueries({ queryKey: key('scans') });
    setScanProblem({ kind: 'failed', job });
  };
  /** The page's polite region: cleared first so the same words are read again. */
  const announce = (text: string) => {
    window.clearTimeout(liveTimer.current);
    setScanLive('');
    liveTimer.current = window.setTimeout(() => setScanLive(text), 60);
  };
  const clearRejoinNote = (target: string) => setScanDone((cur) => (cur === rejoinNote(target) || cur === rejoinNote(target, 'full') ? null : cur));
  // Targets whose full scan the server queued behind a running quick one (POST /scans `queued_mode: 'full'`).
  const fullNext = useRef<Set<string>>(new Set());
  /** The server rejoined a running scan: said once in words (and through the live region) so the button never seems to do nothing. */
  const noteRejoined = (job: ScanJob, queued?: 'full') => {
    if (queued === 'full') fullNext.current.add(job.target.toLowerCase());
    const note = rejoinNote(job.target, queued);
    setScanDone(note); announce(note);
  };
  const scanStarted = (job: ScanJob, opts: { quiet?: boolean; outcome?: ScanStartOutcome; queued_mode?: 'full' } = {}) => {
    setScanProblem(null); setScanDone(null); setScanLost(false); noteProgress(job);
    setScanJobs((cur) => ({ ...cur, [job.connector_id]: job }));
    // The panel announces a start it shows; a start that lands after the panel closed is announced here.
    if (!opts.quiet && panelNow.current !== 'scan' && scanActive(job)) announce(`Scanning ${job.target}`);
    if (!scanActive(job)) void endScan(job);
    else if (opts.outcome === 'rejoined') noteRejoined(job, opts.queued_mode);
  };
  /** After a scan action removes its own control: the panel's control when it is open, else the page heading. */
  const focusAfterScan = (inPanel: 'scan-submit' | 'scan-heading') => setFocusTo(panelNow.current === 'scan' ? inPanel : 'heading');
  /**
   * Replace a stalled scan (no files read for a minute). The server says what it did: restarted, or the
   * scan was still reading and was rejoined (said in words so the button never seems to do nothing).
   */
  const restartScan = async (job: ScanJob) => {
    try {
      const r = await startScan(slug, job.connector_id, { restart: true, repo: job.target });
      scanStarted(r.scan, { quiet: true });
      if (r.outcome === 'rejoined') noteRejoined(r.scan, r.queued_mode);
      else announce(`Scan restarted · ${job.target}`);
    } catch (e) {
      setScanProblem({ kind: 'start', message: `${job.target}: ${errMsg(e, 'The scan could not start.')}` });
    }
    focusAfterScan('scan-heading');
  };
  const scanRows = (jobs: ScanJob[], idPrefix?: string) => jobs.map((j) => (
    <div key={j.job_id} id={idPrefix ? `${idPrefix}${j.connector_id}` : undefined} className="gp-inv-scanning gp-inv-scan-row">
      <span>{scanLine(j)}</span>
      {j.stalled ? (canWriteScan
        ? <Button compact variant="ghost" onClick={() => void restartScan(j)} aria-label={`Scan again · ${j.target}`}>Scan again</Button>
        : <AskAdmin slug={slug} compact verb="scan the code again" what={`runs ${label}`} subject="GAC Pro: scan the code again" />) : null}
    </div>
  ));
  const dismissScanProblem = () => { setScanProblem(null); setFocusTo('heading'); };
  const checkAgain = () => {
    setScanProblem(null); setScanLost(false); setPollRound((r) => r + 1);
    announce('Checking the scan status again');
    focusAfterScan('scan-heading');
  };
  const setBusyFor = (ids: string[], on: boolean) => setBusy((cur) => { const n = new Set(cur); for (const id of ids) { if (on) n.add(id); else n.delete(id); } return n; });
  const clearRow = (id: string) => setRowProblem((p) => { if (!p[id]) return p; const n = { ...p }; delete n[id]; return n; });
  const rowError = (id: string, message: string, action?: Problem['action']) => setRowProblem((p) => ({ ...p, [id]: { message, action } }));
  const openPanel = (p: Panel) => { panelTrigger.current = document.activeElement as HTMLElement | null; setPanel((cur) => (cur === p ? null : p)); };
  const closePanel = () => {
    const t = panel === 'domains' ? heading.current : panelTrigger.current;
    setPanel(null);
    // A trigger that left the page (the domains callout after the last decision) → the heading.
    window.requestAnimationFrame(() => (t && document.contains(t) ? t : heading.current)?.focus());
  };
  const sync = () => {
    if (busy.has('sync')) return;
    setBusyFor(['sync'], true);
    void syncInventory(slug).then(async (r) => { await refresh(); const n = r.sync.connector_resource + r.sync.sbom_component + r.sync.oauth_app; show({ message: `Refreshed from connections · ${n} found` }); })
      .catch((e) => setProblem({ message: errMsg(e, 'The connections could not be read.') })).finally(() => setBusyFor(['sync'], false));
  };

  if (items.isLoading) return <Spinner label="Loading the inventory" />;
  if (items.error || !items.data) {
    return <Alert variant="error"><p>The inventory could not load.</p><Button compact onClick={() => void items.refetch()}>Retry</Button></Alert>;
  }
  const all = items.data.items;
  const canWrite = items.data.can_write;
  const open = all.filter((i) => i.status === 'open');
  const decided = all.filter((i) => i.status !== 'open');
  const progress = inventoryProgress(all);
  const rows = tab === 'open' ? open : onlyFederal ? decided.filter(needsFederalAnswer) : decided;
  const rowIds = rows.map((r) => r.item_id);
  const unknownDomains = (unknown.data?.observations ?? []).filter((o) => !o.matched_provider_key);
  const svcList: Service[] = services.data?.services ?? [];
  const toBoundary = !!scope;
  const addLabel = toBoundary ? `Add to ${label} boundary` : 'Add to services';
  const { high, low } = bulkCandidates(open);

  // ---- decisions ----------------------------------------------------------------------------------------
  const blockedProblem = (blocked: Blocked[]) => {
    const programs = [...new Set(blocked.flatMap((b) => b.programs))];
    const fix = { label: `Take ${blocked.length === 1 ? 'it' : 'them'} out of ${programs.join(' and ')} and undo`, run: () => void forceReopen(blocked.map((b) => b.item)) };
    for (const b of blocked) rowError(b.item.item_id, `${b.item.name} is in the ${b.programs.join(' and ')} boundary.`, fix);
    setProblem({
      message: blocked.length === 1
        ? `Undo needs one more step: ${blocked[0]!.item.name} is in the ${programs.join(' and ')} boundary.`
        : `Undo needs one more step: ${blocked.length} still in a boundary: ${blocked.map((b) => b.item.name).join(', ')}.`,
      action: fix,
    });
  };
  const reopenAll = (list: InventoryRowItem[]) => async () => {
    hideToast();
    const failed: string[] = [];
    const blocked: Blocked[] = [];
    let done = 0;
    for (const item of list) {
      try { await reopenInventory(slug, item.item_id); done++; } catch (e) {
        const b = body(e);
        if (e instanceof ApiError && e.status === 409 && b?.code === 'in_boundary') {
          blocked.push({ item, programs: ((b.programs as Array<{ program: string }> | undefined) ?? []).map((p) => programLabel(p.program)) });
        } else failed.push(item.name);
      }
    }
    await refresh();
    if (blocked.length) blockedProblem(blocked);
    else if (failed.length) setProblem({ message: `Undo failed for ${failed.join(', ')}.` });
    if (done) show({ message: `Undone · ${done === 1 ? list.find((i) => !failed.includes(i.name) && !blocked.some((b) => b.item === i))!.name : `${done} services`}` });
    setFocusTo('heading');
  };
  const forceReopen = async (list: InventoryRowItem[]) => {
    setProblem(null); list.forEach((i) => clearRow(i.item_id));
    const failed: string[] = [];
    for (const item of list) {
      try { await reopenInventory(slug, item.item_id, { remove_from_boundary: true }); }
      catch (e) { failed.push(item.name); rowError(item.item_id, `${item.name}: ${errMsg(e, 'Undo failed.')}`); }
    }
    await refresh();
    const ok = list.length - failed.length;
    if (ok) show({ message: `Undone · ${ok === 1 ? list.find((i) => !failed.includes(i.name))!.name : `${ok} services`}`, details: 'Taken out of the boundary and back in To review' });
    setFocusTo('heading');
  };

  const decide = async (list: InventoryRowItem[], fn: (item: InventoryRowItem) => Promise<unknown>, toastFor: (done: InventoryRowItem[], failed: number) => Omit<ToastState, 'version' | 'undo'>, fallback: string) => {
    if (bulk || !list.length) return;
    const ids = list.map((i) => i.item_id);
    setProblem(null); ids.forEach(clearRow); setBusyFor(ids, true);
    const next = tab === 'open' ? nextFocusId(rowIds, ids) : null;
    const done: InventoryRowItem[] = [];
    if (list.length > 1) setBulk({ done: 0, total: list.length });
    for (const [n, item] of list.entries()) {
      try { await fn(item); done.push(item); } catch (e) {
        const b = body(e);
        const existing = b?.existing as Service | undefined;
        if (e instanceof ApiError && e.status === 409 && existing) {
          rowError(item.item_id, `Already in your services: ${existing.name}.`, { label: `Link to ${existing.name}`, run: () => void link(item, existing) });
        } else rowError(item.item_id, `${item.name}: ${errMsg(e, fallback)}`);
      }
      if (list.length > 1) setBulk({ done: n + 1, total: list.length });
    }
    setBulk(null);
    setBusyFor(ids, false);
    await refresh();
    // Failed rows stay ticked (and marked) so they can be retried.
    setSelected((s) => { const nx = new Set(s); for (const d of done) nx.delete(d.item_id); return nx; });
    const failed = list.length - done.length;
    if (done.length) {
      show({ ...toastFor(done, failed), undo: reopenAll(done) });
      setFocusTo(failed ? list.find((i) => !done.includes(i))!.item_id : next ?? 'heading');
    } else if (list.length === 1) setFocusTo(list[0]!.item_id);
  };
  const names = (l: InventoryRowItem[]) => (l.length === 1 ? l[0]!.name : `${l.length} services`);
  const add = (list: InventoryRowItem[], into: boolean) => {
    // Never a silent "other": items nobody said the function of are skipped and asked.
    const ready = list.filter((i) => !!itemFunction(i));
    const asked = list.filter((i) => !itemFunction(i));
    for (const i of asked) rowError(i.item_id, `Choose what ${i.name} does first.`);
    if (!ready.length) { if (asked[0]) setFocusTo(`fn:${asked[0].item_id}`); return; }
    void decide(ready,
      (item) => acceptInventory(slug, item.item_id, { ...(into && scope ? { scope: { program, period_id: scope.period_id } } : {}) }),
      (d, failed) => ({
        message: `${d.length === 1 && !asked.length && !failed ? `Added ${d[0]!.name}` : `${d.length} added`}${into && scope ? ` to ${label}` : ''}${asked.length ? ` · ${asked.length} need what it does` : ''}`,
        details: [
          `${d.map((i) => i.name).join(', ')} added to your services${into && scope ? ` and the ${label} ${scope.period_id ? 'period' : 'standing'} boundary` : ''}`,
          failed ? `${failed} could not be added; they're marked below` : '',
          asked.length ? `${asked.map((i) => i.name).join(', ')}: choose what it does, then add` : '',
        ].filter(Boolean).join('. '),
      }), 'The service could not be added.');
  };
  const link = (item: InventoryRowItem, s: Service) => void decide([item], (i) => linkInventory(slug, i.item_id, s.external_service_id),
    () => ({ message: `Linked ${item.name}`, details: `${item.name} linked to ${s.name}` }), 'The service could not be linked.');
  const dismiss = (list: InventoryRowItem[], reason: string) => void decide(list, (i) => dismissInventory(slug, i.item_id, reason),
    (d, failed) => ({ message: `Dismissed ${names(d)}`, details: `${d.map((i) => i.name).join(', ')} dismissed: ${reason}${failed ? `. ${failed} could not be dismissed; they're marked below` : ''}` }), 'The service could not be dismissed.');
  const reopenOne = (item: InventoryRowItem) => void (async () => {
    setProblem(null); clearRow(item.item_id); setBusyFor([item.item_id], true);
    const next = nextFocusId(rowIds, [item.item_id]);
    await reopenAll([item])();
    setBusyFor([item.item_id], false);
    setFocusTo(next ?? 'heading');
  })();
  /** A decided service that is not in this program's boundary: put its component there (creating one if none). */
  const addToBoundary = (item: InventoryRowItem) => void (async () => {
    if (!scope || !item.external_service_id || busy.has(item.item_id)) return;
    clearRow(item.item_id); setBusyFor([item.item_id], true);
    try {
      const comps = await qc.fetchQuery({ queryKey: ['boundary', slug, 'components'], queryFn: () => listComponents(slug, program), staleTime: 0 });
      let comp = comps.components.find((c) => c.external_service_id === item.external_service_id) ?? null;
      const svcName = item.review?.service_name ?? item.name;
      if (!comp) {
        comp = (await createComponent(slug, program, { component_key: keyOf(svcName), function: `${svcName}: ${fnLabel(itemFunction(item)).toLowerCase()}`, kind: 'managed_service', external_service_id: item.external_service_id })).component;
      }
      const r = await addToScope(slug, program, { component_id: comp.component_id, period_id: scope.period_id, reason: item.status === 'accepted' ? INVENTORY_SCOPE_REASON : 'Added from the inventory review' });
      await refresh();
      show({ message: `Added ${item.name} to ${label}`, details: `${svcName} is now in the ${label} ${scope.period_id ? 'period' : 'standing'} boundary`,
        undo: async () => { hideToast(); try { await removeFromScope(slug, program, r.scope.scope_id, 'Undone in the inventory review'); await refresh(); show({ message: `Undone · ${item.name}` }); } catch (e) { setProblem({ message: errMsg(e, 'Undo failed.') }); } } });
    } catch (e) { rowError(item.item_id, `${item.name}: ${errMsg(e, 'It could not join the boundary.')}`); } finally { setBusyFor([item.item_id], false); }
  })();
  const onFederalSaved = async (item: InventoryRowItem, question: 'q1' | 'q2', value: string) => {
    await refresh();
    const more = question === 'q1' && value === 'no';
    show({ message: more ? `One more question · ${item.name}` : `Saved · ${item.name}`, details: more ? `${item.name}: could it affect federal data? Answer the next question.` : undefined });
    setFocusTo(more ? `q2:${item.item_id}` : nextFocusId(rowIds, [item.item_id]) ?? 'heading');
  };

  const selectedOpen = open.filter((i) => selected.has(i.item_id));
  const ask = <AskAdmin slug={slug} compact verb="decide inventory items" what={`runs ${label}`} subject={`GAC Pro inventory: decide inventory items`} />;
  const bulkLabel = bulk ? `Adding ${bulk.done} of ${bulk.total}…` : null;

  const findMore = canWrite ? (
    <span className="gp-actions">
      <Button compact aria-expanded={panel === 'scan'} aria-controls="inv-panel-scan" onClick={() => openPanel('scan')}>Scan code</Button>
      <Button compact variant="ghost" aria-busy={busy.has('sync') || undefined} aria-disabled={busy.has('sync') || undefined} onClick={sync}>{busy.has('sync') ? 'Refreshing…' : 'Refresh from connections'}</Button>
      <DisclosureMenu variant="ghost" label="Add what we can't see" choices={[
        { label: 'Answer a few questions', onChoose: () => openPanel('intake') },
        { label: 'Upload a network log', onChoose: () => openPanel('egress') },
      ]} />
    </span>
  ) : null;

  return (
    <Card>
      <div className="gp-inv" ref={wrap}>
        <button type="button" className="gp-cb-back" onClick={onBack}><span aria-hidden="true">‹ </span>Back to the boundary</button>
        <div className="gp-cb-head">
          <h3 className="gp-card-h" id="inv-h" ref={heading} tabIndex={-1}>Services your product depends on</h3>
          {onOpenVerdicts && decided.some((i) => i.status === 'accepted' || i.status === 'linked') ? <Button compact variant="ghost" onClick={onOpenVerdicts}>See the verdicts</Button> : null}
          {/* One bulk surface: the header's while nothing is ticked, the selection bar once something is. */}
          {canWrite && tab === 'open' && !selectedOpen.length && (high.length || low.length) ? (
            high.length ? (
              <SplitButton variant="primary" label={bulkLabel ?? `Add ${high.length} high-confidence ${toBoundary ? `to ${label}` : 'to services'}`} onClick={() => add(high, toBoundary)}
                caretLabel="More bulk actions" choices={[
                  ...(toBoundary ? [{ label: `Add ${high.length} high-confidence to services only`, onChoose: () => add(high, false), disabled: !!bulk }] : []),
                  ...(low.length ? [{ label: `Dismiss ${low.length} low-confidence · not used by the product`, onChoose: () => dismiss(low, 'Not used by the product'), disabled: !!bulk }] : []),
                ]} />
            ) : <DisclosureMenu label={`Dismiss ${low.length} low-confidence`} choices={DISMISS_REASONS.map((r) => ({ label: r, onChoose: () => dismiss(low, r), disabled: !!bulk }))} />
          ) : null}
        </div>
        {!canWrite ? (
          <Alert variant="info"><p>You can see this list. An owner, an admin or the program lead decides each service.</p>{ask}</Alert>
        ) : null}
        {canWrite && !scope && scopeNote ? <p className="gp-dim gp-inv-filter">{`Adds go to services only: ${scopeNote}.`}</p> : null}
        {/* With nothing found yet the empty state carries these (and its Scan code is the one primary). */}
        {canWrite && all.length ? findMore : null}
        {/* The panel was closed while scans run: one quiet line each (the start was announced in the panel). */}
        {panel !== 'scan' && !scanLost ? scanRows(Object.values(scanJobs).filter(scanActive)) : null}
        {scanDone ? (
          <div className="gp-inv-scan-done">{scanDone} <Button compact variant="ghost" onClick={() => { setScanDone(null); setFocusTo('heading'); }} aria-label={`Dismiss · ${scanDone}`}>Dismiss</Button></div>
        ) : null}
        <p className="sr-only" role="status" aria-live="polite">{scanLive}</p>
        {scanProblem && panel !== 'scan' ? (
          <ScanProblemBanner slug={slug} prefix={prefix} canWrite={canWriteScan} askWhat={`runs ${label}`} problem={scanProblem} onDismiss={dismissScanProblem}
            onScanAgain={() => { setScanProblem(null); setScanPanelFor('job' in scanProblem ? scanProblem.job.connector_id : null); openPanel('scan'); }}
            onCheckAgain={checkAgain} />
        ) : null}
        {problem ? (
          <Alert variant="error" onDismiss={() => setProblem(null)}>
            <p>{problem.message}</p>
            {problem.action ? <Button compact onClick={() => { const a = problem.action!; setProblem(null); a.run(); }}>{problem.action.label}</Button> : null}
          </Alert>
        ) : null}

        {unknownDomains.length ? (
          <div className="gp-cb-callout" data-tone="warn">
            <span><b>{`${unknownDomains.length} outbound domain${unknownDomains.length === 1 ? '' : 's'} we can't name`}</b> <span className="gp-dim">from your network logs</span></span>
            <span className="gp-actions">
              {canWrite ? <Button compact aria-expanded={panel === 'domains'} aria-controls="inv-panel-domains" onClick={() => openPanel('domains')}>{`Decide ${unknownDomains.length}`}</Button> : null}
              <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" to={`${prefix}/findings?kind=infra_unknown_egress`}>Open findings</Link>
            </span>
          </div>
        ) : null}

        {panel === 'domains' ? (
          <DomainsPanel slug={slug} domains={unknownDomains} onClose={closePanel} onIntake={() => { setPanel('intake'); }}
            onDone={async (domain, msg) => {
              await refresh();
              show({ message: msg, undo: async () => { hideToast(); try { await undoEgress(slug, domain); await refresh(); show({ message: `Undone · ${domain}` }); } catch (e) { setProblem({ message: `${domain}: ${errMsg(e, 'Undo failed.')}` }); } } });
            }} />
        ) : null}
        {panel === 'scan' ? <ScanPanel slug={slug} prefix={prefix} jobs={scanJobs} initialId={scanPanelFor} lost={scanLost} rows={(jobs) => scanRows(jobs, 'inv-scan-line-')} returned={githubReturn}
          problem={scanProblem} askWhat={`runs ${label}`} canWrite={canWriteScan} onCheckAgain={checkAgain}
          onDismissProblem={(next) => { setScanProblem(null); setFocusTo(next); }}
          onClose={() => { setScanPanelFor(null); setGithubReturn(null); closePanel(); }}
          onStarted={(job, reply) => scanStarted(job, reply ?? {})}
          onStartFailed={(message) => { if (panelNow.current !== 'scan') setScanProblem({ kind: 'start', message }); }} /> : null}
        {panel === 'intake' ? <IntakePanel slug={slug} onClose={closePanel} onSaved={async (q) => { await refresh(); show({ message: `Saved · ${q}` }); }} /> : null}
        {panel === 'egress' ? <EgressPanel slug={slug} onClose={closePanel} onDone={async (t) => { await refresh(); setPanel(null); show(t); setFocusTo('heading'); }} /> : null}

        <div className="gp-inv-tabs" role="group" aria-label="Show">
          <button type="button" aria-pressed={tab === 'open'} onClick={() => { setTab('open'); setOnlyFederal(false); }}>{`To review · ${open.length}`}</button>
          <button type="button" aria-pressed={tab === 'decided'} onClick={() => setTab('decided')}>{`Decided · ${decided.length}`}</button>
        </div>

        {tab === 'decided' && onlyFederal && rows.length ? (
          <p className="gp-inv-filter" role="status">
            {`Showing ${rows.length} that need the federal-data answer`} <button type="button" className="gp-cb-textbtn" onClick={() => setOnlyFederal(false)}>Show all</button>
          </p>
        ) : null}

        <p className="sr-only" role="status" aria-live="polite">{canWrite && selectedOpen.length && tab === 'open' ? `${selectedOpen.length} selected · actions above the list` : ''}</p>
        {canWrite && selectedOpen.length && tab === 'open' ? (
          <div className="gp-inv-bar" role="group" aria-label={`${selectedOpen.length} selected`}>
            <span><b>{`${selectedOpen.length} selected`}</b></span>
            <Button compact variant="primary" aria-disabled={!!bulk || undefined} onClick={() => add(selectedOpen, toBoundary)}>
              {bulkLabel ?? (toBoundary ? `Add ${selectedOpen.length} to ${label} boundary` : `Add ${selectedOpen.length} to services`)}
            </Button>
            <DisclosureMenu variant="ghost" label={`Dismiss ${selectedOpen.length}`} choices={DISMISS_REASONS.map((r) => ({ label: r, onChoose: () => dismiss(selectedOpen, r), disabled: !!bulk }))} />
            <button type="button" className="gp-cb-textbtn" onClick={() => { setSelected(new Set()); setFocusTo('heading'); }}>Clear</button>
          </div>
        ) : null}

        {tab === 'open' && open.length === 0 && all.length === 0 ? (
          <EmptyState title="No services found yet" description="Scan your code, refresh from your connections, upload a network log or answer a few questions."
            action={canWrite ? <span className="gp-actions">
              <Button compact variant="primary" onClick={() => openPanel('scan')}>Scan code</Button>
              <Button compact variant="ghost" aria-busy={busy.has('sync') || undefined} aria-disabled={busy.has('sync') || undefined} onClick={sync}>{busy.has('sync') ? 'Refreshing…' : 'Refresh from connections'}</Button>
              <Button compact variant="ghost" onClick={() => openPanel('egress')}>Upload a network log</Button>
              <Button compact variant="ghost" onClick={() => openPanel('intake')}>Answer a few questions</Button>
            </span> : undefined} />
        ) : (tab === 'open' && open.length === 0) || (onlyFederal && rows.length === 0 && !open.length) ? (
          progress.needsFederal ? (
            <div className="gp-cb-callout" data-tone="warn">
              <span><b>{`${progress.needsFederal} service${progress.needsFederal === 1 ? '' : 's'} need the federal-data answer`}</b> <span className="gp-dim">it decides each verdict</span></span>
              <Button compact variant="primary" onClick={() => { setTab('decided'); setOnlyFederal(true); setFocusTo('heading'); }}>{canWrite ? 'Answer them' : 'Show them'}</Button>
            </div>
          ) : (
            <EmptyState title="Inventory reviewed" description={`${progress.added} added, ${progress.linked} linked, ${progress.dismissed} dismissed`}
              action={<span className="gp-actions">
                {onContinue ? <Button compact variant="primary" onClick={onContinue}>Continue to verdicts</Button> : null}
                <Button compact variant={onContinue ? "ghost" : "primary"} onClick={onBack}>Back to the boundary</Button>
              </span>} />
          )
        ) : rows.length === 0 ? (
          <EmptyState title={onlyFederal ? 'Every service has its federal-data answer' : 'Nothing decided yet'}
            action={<Button compact onClick={() => { setTab('open'); setOnlyFederal(false); }}>{open.length ? 'Review the open ones' : 'Back to the list'}</Button>} />
        ) : (
          <ul className="gp-cb-rows" aria-labelledby="inv-h">
            {rows.map((item) => (
              <InventoryRow key={item.item_id} slug={slug} item={item} canWrite={canWrite} busy={busy.has(item.item_id) || !!bulk} services={svcList} addLabel={addLabel} toBoundary={toBoundary}
                programName={label} program={program} full={onlyFederal}
                selected={selected.has(item.item_id)} onSelect={(on) => setSelected((s) => { const n = new Set(s); if (on) n.add(item.item_id); else n.delete(item.item_id); return n; })}
                problem={rowProblem[item.item_id] ?? null}
                onAdd={(into) => add([item], into)} onLink={(s) => link(item, s)} onDismiss={(r) => dismiss([item], r)} onReopen={() => reopenOne(item)}
                onAddToBoundary={() => addToBoundary(item)}
                onFunction={async (fn) => {
                  clearRow(item.item_id);
                  try { await setInventoryFunction(slug, item.item_id, fn); await refresh(); show({ message: `Saved · ${item.name} does ${fnLabel(fn).toLowerCase()}` }); }
                  catch (e) { rowError(item.item_id, `${item.name}: ${errMsg(e, 'Could not save what it does.')}`); }
                }}
                onFederal={(q, v) => onFederalSaved(item, q, v)}
                onAnswered={async (msg) => { await refresh(); show({ message: msg }); }}
                setRowError={(m) => (m ? rowError(item.item_id, m) : clearRow(item.item_id))} />
            ))}
          </ul>
        )}
      </div>
      {toast && !toastApi ? <Toast message={toast.message} details={toast.details} version={toast.version} returnFocus={heading.current}
        onUndo={toast.undo ? () => void toast.undo!() : undefined} onDismiss={() => setToast(null)} /> : null}
    </Card>
  );
}

// ---- one row ------------------------------------------------------------------------------------------

function InventoryRow({ slug, item, canWrite, busy, services, addLabel, toBoundary, programName, program, full, selected, onSelect, problem, onAdd, onLink, onDismiss, onReopen, onAddToBoundary, onFunction, onFederal, onAnswered, setRowError }: {
  slug: string; item: InventoryRowItem; canWrite: boolean; busy: boolean; services: Service[]; addLabel: string; toBoundary: boolean;
  programName: string; program: string; full: boolean;
  selected: boolean; onSelect: (on: boolean) => void; problem: Problem | null;
  onAdd: (intoBoundary: boolean) => void; onLink: (s: Service) => void; onDismiss: (reason: string) => void; onReopen: () => void; onAddToBoundary: () => void;
  onFunction: (fn: string) => Promise<void>; onFederal: (q: 'q1' | 'q2', v: string) => Promise<void>; onAnswered: (msg: string) => Promise<void>; setRowError: (m: string | null) => void;
}) {
  const kinds = [...new Set(item.sources.filter((s) => !s.gone_at).map((s) => s.source_kind))];
  const fn = itemFunction(item);
  const chip = federalChip(item);
  const existing = item.review?.existing_service ?? null;
  const isOpen = item.status === 'open';
  const [picking, setPicking] = useState(false);
  const [other, setOther] = useState<string | null>(null);
  const li = useRef<HTMLLIElement>(null);
  const linkBtn = useRef<HTMLButtonElement>(null);
  const hintId = `inv-fn-hint-${item.item_id}`;
  const pickerId = `inv-link-${item.item_id}`;
  // The row asks the federal-data question while it is incomplete (not for dismissed rows); "Answer them" shows the full form.
  const pending = canWrite && item.status !== 'dismissed' && federalPending(item);
  const quick = pending && !full;
  const fnSel = useCommitSelect(item.function ?? '', (v) => { if (v && v !== (item.function ?? '')) void onFunction(v); });
  const focusDismiss = () => window.requestAnimationFrame(() => li.current?.querySelector<HTMLElement>(`[data-dismiss-for="${item.item_id}"] button`)?.focus());
  const guardAdd = (into: boolean) => {
    if (busy) return;
    if (!fn) { setRowError(`Choose what ${item.name} does first.`); li.current?.querySelector<HTMLSelectElement>('.gp-inv-fn select')?.focus(); return; }
    onAdd(into);
  };
  const where = item.review?.boundary_programs ?? [];
  const outside = toBoundary && !!item.external_service_id && (item.status === 'accepted' || item.status === 'linked') && !where.includes(program);
  const decidedLine = item.status === 'accepted' ? 'Added to your services'
    : item.status === 'linked' ? `Linked to ${item.review?.service_name ?? 'a service'}` : item.status === 'dismissed' ? `Dismissed: ${item.dismiss_reason ?? ''}` : null;
  const addBusy = busy ? 'Adding…' : addLabel;
  return (
    <li className="gp-cb-sg" data-item-id={item.item_id} ref={li}>
      {canWrite && isOpen ? (
        <span className="gp-inv-tick"><Checkbox id={`inv-sel-${item.item_id}`} label={<span className="sr-only">{`Select ${item.name}`}</span>} checked={selected} onChange={(e) => onSelect(e.currentTarget.checked)} /></span>
      ) : null}
      <span className="gp-cb-main">
        <span className="gp-inv-title">
          <b>{item.name}</b>
          {/* While the row asks the question itself, the chip would only repeat it. */}
          {quick ? null : <span className="gp-cb-chip" data-tone={chip.tone}>{chip.label}</span>}
          {isOpen && existing ? <span className="gp-cb-chip" data-tone="info">{`Looks like ${existing.name}`}</span> : null}
        </span>
        <span className="gp-cb-meta">
          <span className="sr-only">Found by: </span>
          <span className="gp-cb-tags">{kinds.map((k) => <span key={k} className="gp-cb-tag">{SOURCE_LABEL[k] ?? 'Other source'}</span>)}</span>
          <span aria-hidden="true">·</span>
          <span>{`${confidenceLabel(item.confidence)} confidence`}</span>
          <span aria-hidden="true">·</span>
          {canWrite && isOpen ? (
            <span className="gp-inv-fn">
              <Select aria-label={`What ${item.name} does`} aria-describedby={fn ? undefined : hintId} {...fnSel.props}>
                <option value="">{item.suggested_function ? `${fnLabel(item.suggested_function)} (suggested)` : 'Choose what it does'}</option>
                {Object.entries(FUNCTION_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
              {fn ? null : <span id={hintId} className="sr-only">Needed before it can be added</span>}
            </span>
          ) : <span>{fn ? fnLabel(fn) : 'What it does: not set'}</span>}
        </span>
        {decidedLine ? (
          <span className="gp-inv-decided">
            <span>{decidedLine}{outside ? <span className="gp-dim">{` · not in the ${programName} boundary`}</span> : null}{item.decided_at ? <span className="gp-dim">{` · ${item.decided_by_name ? `by ${item.decided_by_name} · ` : ''}${fmtDate(item.decided_at)}`}</span> : null}</span>
            {outside && canWrite ? <button type="button" className="gp-cb-textbtn" onClick={onAddToBoundary} aria-label={`Add to ${programName} boundary · ${item.name}`}>{busy ? 'Adding…' : `Add to ${programName} boundary`}</button> : null}
          </span>
        ) : null}
        {problem ? (
          <Alert variant="error" onDismiss={() => setRowError(null)}>
            <p>{problem.message}</p>
            {problem.action ? <Button compact onClick={problem.action.run}>{problem.action.label}</Button> : null}
          </Alert>
        ) : null}
        {quick ? <QuickFederal slug={slug} item={item} onSaved={onFederal} onAnswered={onAnswered} setError={setRowError} /> : null}
        {pending && full ? <ClassificationForm slug={slug} item={item} canWrite onAnswered={async (m) => { await onAnswered(m); }} setError={setRowError} /> : null}
        {picking ? (
          <LinkPicker id={pickerId} item={item} services={services} onPick={(s) => { setPicking(false); onLink(s); }}
            onAddInstead={isOpen && !existing ? () => { setPicking(false); guardAdd(toBoundary); } : undefined}
            onClose={() => { setPicking(false); window.requestAnimationFrame(() => linkBtn.current?.focus()); }} />
        ) : null}
        {other !== null ? (
          <form className="gp-inline-form" data-esc-owner="" onSubmit={(e) => { e.preventDefault(); if (other.trim().length < 3) return; const r = other.trim(); setOther(null); onDismiss(r); }}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOther(null); focusDismiss(); } }} aria-label={`Dismiss ${item.name}: other reason`}>
            <Field id={`inv-other-${item.item_id}`} label="Why it is not part of the product" hint="At least 3 characters.">
              <Input autoFocus value={other} onChange={(e) => setOther(e.currentTarget.value)} />
            </Field>
            <div className="gp-actions">
              <Button type="submit" compact variant="primary" aria-disabled={other.trim().length < 3 || undefined} aria-describedby={`inv-other-${item.item_id}-hint`}>Dismiss</Button>
              <Button compact variant="ghost" onClick={() => { setOther(null); focusDismiss(); }}>Cancel</Button>
            </div>
          </form>
        ) : null}
        <details className="gp-cb-why">
          <summary>Where it was found<span className="sr-only">{` · ${item.name}`}</span></summary>
          <SourceList item={item} />
          {!pending ? <ClassificationForm slug={slug} item={item} canWrite={canWrite} onAnswered={onAnswered} setError={setRowError} /> : null}
        </details>
      </span>
      {canWrite ? (
        <span className="gp-cb-act">
          {isOpen ? (
            existing ? (
              <>
                <Button compact aria-busy={busy || undefined} aria-disabled={busy || undefined} onClick={() => { if (!busy) onLink(existing); }} aria-label={`${busy ? 'Linking…' : `Link to ${existing.name}`} · ${item.name}`}>{busy ? 'Linking…' : `Link to ${existing.name}`}</Button>
                <Button ref={linkBtn} compact variant="ghost" aria-expanded={picking} aria-controls={pickerId} onClick={() => setPicking(!picking)} aria-label={`Link to another service · ${item.name}`}>Another service</Button>
                <span data-dismiss-for={item.item_id}><DisclosureMenu variant="ghost" label="Dismiss" ariaLabel={`Dismiss · ${item.name}`} choices={[...DISMISS_REASONS.map((r) => ({ label: r, onChoose: () => onDismiss(r) })), { label: 'Other…', onChoose: () => setOther('') }]} /></span>
              </>
            ) : (
              <>
                {toBoundary ? (
                  <SplitButton label={addBusy} ariaLabel={`${addBusy} · ${item.name}`} onClick={() => guardAdd(true)} caretLabel={`More ways to add · ${item.name}`}
                    choices={[{ label: 'Add to services only', onChoose: () => guardAdd(false) }]} />
                ) : (
                  <Button compact aria-busy={busy || undefined} aria-disabled={busy || undefined} onClick={() => guardAdd(false)} aria-label={`${addBusy} · ${item.name}`}>{addBusy}</Button>
                )}
                <Button ref={linkBtn} compact variant="ghost" aria-expanded={picking} aria-controls={pickerId} onClick={() => setPicking(!picking)} aria-label={`Link to a service you already have · ${item.name}`}>Link…</Button>
                <span data-dismiss-for={item.item_id}><DisclosureMenu variant="ghost" label="Dismiss" ariaLabel={`Dismiss · ${item.name}`} choices={[
                  ...DISMISS_REASONS.map((r) => ({ label: r, onChoose: () => onDismiss(r) })),
                  { label: 'It duplicates a service: link it instead', onChoose: () => setPicking(true) },
                  { label: 'Other…', onChoose: () => setOther('') },
                ]} /></span>
              </>
            )
          ) : (
            <button type="button" className="gp-cb-textbtn" onClick={() => { if (!busy) onReopen(); }} aria-label={`Reopen · ${item.name}`}>{busy ? 'Reopening…' : 'Reopen'}</button>
          )}
        </span>
      ) : null}
    </li>
  );
}

/** The workspace's services with a filter: no silent truncation; the count is announced once typing settles. */
function LinkPicker({ id, item, services, onPick, onClose, onAddInstead }: { id: string; item: InventoryRowItem; services: Service[]; onPick: (s: Service) => void; onClose: () => void; onAddInstead?: () => void }) {
  const [q, setQ] = useState('');
  const list = filterByName(services, q);
  const said = useSettled(q.trim() ? (list.length ? `${list.length} service${list.length === 1 ? '' : 's'} match` : `No service matches “${q.trim()}”`) : '');
  return (
    <div className="gp-inv-panel gp-inv-link" id={id} role="group" aria-label={`Link ${item.name} to a service you already have`} data-esc-owner=""
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
      {services.length === 0 ? (
        <p className="gp-inv-nomatch">No services in this workspace yet. {onAddInstead ? <button type="button" className="gp-cb-textbtn" onClick={onAddInstead}>Add it instead</button> : null}</p>
      ) : (
        <>
          <Field id={`${id}-q`} label="Find a service" hint={`${list.length} of ${services.length}`}>
            <Input autoFocus value={q} onChange={(e) => setQ(e.currentTarget.value)} />
          </Field>
          <p className="sr-only" role="status" aria-live="polite">{said}</p>
          {list.length ? (
            <ul className="gp-inv-picks">
              {list.map((s) => <li key={s.external_service_id}><button type="button" className="gp-inv-pick" onClick={() => onPick(s)}>{s.name}</button></li>)}
            </ul>
          ) : <p className="gp-inv-nomatch">No match. {onAddInstead ? <button type="button" className="gp-cb-textbtn" onClick={onAddInstead}>Add it instead</button> : null}</p>}
        </>
      )}
      <div className="gp-actions"><Button compact variant="ghost" onClick={onClose}>Cancel</Button></div>
    </div>
  );
}

function SourceList({ item }: { item: InventoryRowItem }) {
  const lines: string[] = [];
  const has = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== '';
  for (const s of item.sources.filter((x) => !x.gone_at)) {
    const d = s.detail as Record<string, unknown>;
    if (s.source_kind === 'repo_scan') {
      const hits = (Array.isArray(d.hits) ? d.hits : []) as Array<{ path?: string; line?: number }>;
      const paths = [...new Set(hits.filter((h) => has(h.path)).map((h) => (has(h.line) ? `${h.path}:${h.line}` : String(h.path))))].slice(0, 3);
      lines.push(paths.length ? `Code · ${paths.join(', ')}${hits.length > 3 ? '…' : ''}` : 'Code');
    } else if (s.source_kind === 'connector_resource') lines.push(has(d.resource_count) ? `Cloud · ${String(d.resource_count)} resource${d.resource_count === 1 ? '' : 's'}` : 'Cloud');
    else if (s.source_kind === 'sbom_component') { const c = (Array.isArray(d.components) ? d.components : []).filter(has).slice(0, 2); lines.push(c.length ? `SBOM · ${c.join(', ')}` : 'SBOM'); }
    else if (s.source_kind === 'oauth_app') lines.push(['Sign-in apps', has(d.name) ? String(d.name) : null, d.user_count ? `${String(d.user_count)} users` : null].filter(Boolean).join(' · '));
    else if (s.source_kind === 'egress') lines.push(has(d.domain) ? `Network · ${String(d.domain)}` : 'Network');
    else if (s.source_kind === 'intake') lines.push('You said so in the questions');
  }
  return lines.length ? <ul className="gp-inv-src">{[...new Set(lines)].map((l) => <li key={l}>{l}</li>)}</ul> : null;
}

// ---- federal data (Q1 / Q2) -----------------------------------------------------------------------------

/**
 * The row's compact question: one labelled select that saves when the choice is committed (Q1;
 * Q2 once Q1 = no), a waiting suggestion with Confirm, and "Suggest" as a text button.
 * Answers are attested and append-only: a wrong one is changed (not deleted) under
 * "Where it was found", so there is no Undo here.
 */
function QuickFederal({ slug, item, onSaved, onAnswered, setError }: {
  slug: string; item: InventoryRowItem; onSaved: (q: 'q1' | 'q2', v: string) => Promise<void>; onAnswered: (msg: string) => Promise<void>; setError: (e: string | null) => void;
}) {
  const facts = item.review?.federal ?? { q1: null, q2: null, suggested: false };
  const q: 'q1' | 'q2' = facts.q1 === 'no' ? 'q2' : 'q1';
  const labels = q === 'q1' ? Q1_LABEL : Q2_LABEL;
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [note, setNote] = useState('');
  const answers = useQuery({ queryKey: ['infra-plan', slug, 'answers', item.item_id], queryFn: () => getItemAnswers(slug, item.item_id), enabled: facts.suggested });
  const waiting = (answers.data?.answers ?? []).find((a) => a.question === q && a.status === 'suggested');
  const commit = (v: string) => {
    if (!v || saving || v === (facts[q] ?? '')) return;
    setSaving(true); setError(null);
    void answerItem(slug, item.item_id, q, v).then(() => onSaved(q, v)).catch((e) => setError(`${item.name}: ${errMsg(e, 'The answer could not be saved.')}`)).finally(() => setSaving(false));
  };
  const sel = useCommitSelect('', commit);
  const suggest = async () => {
    if (suggesting) return;
    setSuggesting(true); setNote(''); setError(null);
    try {
      const r = await suggestItem(slug, item.item_id);
      if (r.status === 'abstained') setNote(`No suggestion: ${r.reason}`);
      else { await answers.refetch(); await onAnswered(`Suggestion ready · ${item.name}`); }
    } catch (e) {
      const b = body(e) as { code?: string } | undefined;
      if (e instanceof ApiError && e.status === 409 && (b?.code === 'federal_off' || b?.code === 'no_ai_provider')) setNote('Suggestions are off for this workspace’s data. Answer below.');
      else setError(`${item.name}: ${errMsg(e, 'No suggestion could be made.')}`);
    } finally { setSuggesting(false); }
  };
  const what = q === 'q1' ? 'Holds federal data' : 'Could affect federal data';
  return (
    <span className="gp-inv-quick">
      <Select data-q={q} aria-label={`${what} · ${item.name}`} aria-busy={saving || undefined} {...sel.props}>
        <option value="">{q === 'q1' ? 'Holds federal data?' : 'Could it affect that data?'}</option>
        {Object.entries(labels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Select>
      {waiting ? (
        <>
          <span>{`Suggested: ${labels[String(waiting.value)] ?? 'an answer'}`}</span>
          <Button compact aria-disabled={saving || undefined} onClick={() => { if (!saving) { setSaving(true); void confirmAnswer(slug, waiting.answer_id).then(() => onSaved(q, String(waiting.value))).catch((e) => setError(`${item.name}: ${errMsg(e, 'Could not confirm.')}`)).finally(() => setSaving(false)); } }}
            aria-label={`Confirm: ${what.toLowerCase()} · ${item.name}`}>Confirm</Button>
        </>
      ) : (
        <button type="button" className="gp-cb-textbtn" aria-busy={suggesting || undefined} onClick={() => void suggest()} aria-label={`Suggest an answer · ${item.name}`}>{suggesting ? 'Suggesting…' : 'Suggest'}</button>
      )}
      {note ? <span className="gp-dim">{note}</span> : null}
      <span className="sr-only" role="status" aria-live="polite">{note}</span>
    </span>
  );
}

export function ClassificationForm({ slug, item, canWrite, onAnswered, setError }: {
  slug: string; item: Pick<InventoryRowItem, 'item_id' | 'name'> & { review: { federal: NonNullable<InventoryRowItem['review']>['federal'] } | null }; canWrite: boolean; onAnswered: (msg: string) => Promise<void>; setError: (e: string | null) => void;
}) {
  const facts = item.review?.federal ?? { q1: null, q2: null, suggested: false };
  const [q1, setQ1] = useState<string>(facts.q1 ?? '');
  const [q2, setQ2] = useState<string>(facts.q2 ?? '');
  const [note, setNote] = useState<{ tone: 'info' | 'warn'; text: string; href?: string; label?: string } | null>(null);
  const [live, setLive] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [asked, setAsked] = useState(false);
  const askQ2 = (facts.q1 ?? q1) === 'no';
  // Full answers (suggestions + citations, the Q2 default) only when they matter: a suggestion waits, Q2 shows, or one was just asked for.
  const answers = useQuery({ queryKey: ['infra-plan', slug, 'answers', item.item_id], queryFn: () => getItemAnswers(slug, item.item_id), enabled: facts.suggested || askQ2 || asked });
  useEffect(() => { setQ1(facts.q1 ?? ''); }, [facts.q1]);
  useEffect(() => { setQ2(facts.q2 ?? ''); }, [facts.q2]);
  useEffect(() => { if (askQ2 && !q2 && answers.data?.default_q2) setQ2(answers.data.default_q2); }, [askQ2, answers.data?.default_q2, q2]);
  const list = answers.data?.answers ?? [];
  const suggested = (q: string) => list.find((a) => a.question === q && a.status === 'suggested');
  const whyNot = (v: string, current: string | null) => (!v ? 'Choose an answer first.' : v === current ? 'That is the saved answer.' : '');

  const save = async (question: 'q1' | 'q2', value: string) => {
    if (!value || saving || value === (question === 'q1' ? facts.q1 : facts.q2)) return;
    setError(null); setSaving(true);
    try {
      await answerItem(slug, item.item_id, question, value);
      await answers.refetch().catch(() => undefined);
      await onAnswered(question === 'q1' && value === 'no' && !facts.q2 ? `One more question · ${item.name}` : `Saved · ${item.name}`);
    } catch (e) { setError(`${item.name}: ${errMsg(e, 'The answer could not be saved.')}`); } finally { setSaving(false); }
  };
  const confirm = async (answerId: string) => {
    if (saving) return;
    setError(null); setSaving(true);
    try { await confirmAnswer(slug, answerId); await answers.refetch(); await onAnswered(`Confirmed · ${item.name}`); }
    catch (e) { setError(`${item.name}: ${errMsg(e, 'The answer could not be confirmed.')}`); } finally { setSaving(false); }
  };
  const suggest = async () => {
    if (suggesting) return;
    setError(null); setNote(null); setSuggesting(true); setAsked(true); setLive('');
    try {
      const r = await suggestItem(slug, item.item_id);
      if (r.status === 'abstained') { setNote({ tone: 'info', text: r.reason }); setLive(`No suggestion for ${item.name}: ${r.reason}`); }
      else { await answers.refetch(); await onAnswered(`Suggestion ready · ${item.name}`); setLive(`Suggestion ready for ${item.name}. Confirm it below.`); }
    } catch (e) {
      const b = body(e) as { code?: string; action?: { label: string; href: string } | null } | undefined;
      if (e instanceof ApiError && e.status === 409 && (b?.code === 'federal_off' || b?.code === 'no_ai_provider')) {
        const text = 'Suggestions are off for this workspace’s data. Answer below.';
        setNote({ tone: 'warn', text, href: b.action?.href, label: b.action?.label });
        setLive(text);
      } else setError(`${item.name}: ${errMsg(e, 'No suggestion could be made.')}`);
    } finally { setSuggesting(false); }
  };

  const suggestion = (q: 'q1' | 'q2', labels: Record<string, string>) => {
    const s = suggested(q);
    if (!s) return null;
    const why = s.citations.map((c) => c.text).filter((t): t is string => !!t).slice(0, 2);
    const what = q === 'q1' ? 'holds federal data' : 'could affect federal data';
    return (
      <div className="gp-inv-sugg" role="group" aria-label={`Suggested answer: ${item.name} ${what}`}>
        <span><b>{`Suggested: ${labels[String(s.value)] ?? 'an answer'}`}</b> <span className="gp-dim">· needs your confirmation</span></span>
        {why.length ? <><span className="gp-dim">Why we suggest this</span><ul className="gp-inv-src">{why.map((t) => <li key={t}>{t}</li>)}</ul></> : null}
        {canWrite ? <Button compact variant="primary" aria-disabled={saving || undefined} onClick={() => void confirm(s.answer_id)} aria-label={`Confirm: ${item.name} ${what}`}>{saving ? 'Saving…' : 'Confirm'}</Button> : null}
      </div>
    );
  };

  if (!canWrite) {
    const ro = (v: string | null, labels: Record<string, string>) => (v ? labels[v] ?? 'Answered' : 'Not answered');
    return <p className="gp-inv-fed">{`Holds federal data: ${ro(facts.q1, Q1_LABEL)}${facts.q1 === 'no' ? ` · Could affect it: ${ro(facts.q2, Q2_LABEL)}` : ''}`}</p>;
  }
  const w1 = whyNot(q1, facts.q1);
  const w2 = whyNot(q2, facts.q2);
  return (
    <div className="gp-inv-fed">
      <p className="sr-only" role="status" aria-live="polite">{live}</p>
      {note ? <Alert variant={note.tone === 'warn' ? 'warning' : 'info'}><p>{note.text}</p>{note.href ? <Link to={note.href}>{note.label ?? 'Set it up'}</Link> : null}</Alert> : null}
      {suggestion('q1', Q1_LABEL)}
      <form className="gp-inline-form gp-inv-q1" onSubmit={(e: FormEvent) => { e.preventDefault(); void save('q1', q1); }}>
        <Field id={`q1-${item.item_id}`} label={`Does ${item.name} hold federal customer data?`}>
          <Select value={q1} onChange={(e) => setQ1(e.currentTarget.value)}>
            <option value="">Choose</option>
            {Object.entries(Q1_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </Field>
        <span id={`q1-${item.item_id}-why`} className="sr-only">{w1}</span>
        <div className="gp-actions">
          <Button type="submit" compact variant="primary" aria-disabled={saving || !!w1 || undefined} aria-describedby={w1 ? `q1-${item.item_id}-why` : undefined}>{saving ? 'Saving…' : 'Save answer'}</Button>
          <Button compact variant="ghost" aria-busy={suggesting || undefined} aria-disabled={suggesting || undefined} onClick={() => void suggest()}>{suggesting ? 'Suggesting…' : 'Suggest an answer'}</Button>
        </div>
      </form>
      {askQ2 ? (
        <>
          {suggestion('q2', Q2_LABEL)}
          <form className="gp-inline-form" onSubmit={(e: FormEvent) => { e.preventDefault(); void save('q2', q2); }}>
            <Field id={`q2-${item.item_id}`} label={`Could ${item.name} affect that data’s confidentiality, integrity or availability?`} hint="Identity, build and edge services usually can.">
              <Select data-q="q2" value={q2} onChange={(e) => setQ2(e.currentTarget.value)}>
                <option value="">Choose</option>
                {Object.entries(Q2_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
            <span id={`q2-${item.item_id}-why`} className="sr-only">{w2}</span>
            <div className="gp-actions"><Button type="submit" compact variant="primary" aria-disabled={saving || !!w2 || undefined} aria-describedby={w2 ? `q2-${item.item_id}-why` : undefined}>{saving ? 'Saving…' : 'Save answer'}</Button></div>
          </form>
        </>
      ) : null}
    </div>
  );
}

// ---- panels -------------------------------------------------------------------------------------------------

/** A panel's heading takes focus when it opens (and scrolls into view); closing returns focus to the trigger (the parent). */
function usePanelFocus() {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, []);
  return ref;
}

/**
 * Unknown domains, one row each. A decision removes the row: focus goes to the next row (else
 * the previous, else "Done" under "All domains decided"), never to the page body.
 */
function DomainsPanel({ slug, domains, onClose, onDone, onIntake }: {
  slug: string; domains: Array<{ domain: string; hits: number }>; onClose: () => void; onDone: (domain: string, msg: string) => Promise<void>; onIntake: () => void;
}) {
  const h = usePanelFocus();
  const wrap = useRef<HTMLDivElement>(null);
  const done = useRef<HTMLButtonElement>(null);
  const [focusDomain, setFocusDomain] = useState<string | null>(null);
  const list = [...new Map(domains.map((d) => [d.domain, d])).values()];
  const names = list.map((d) => d.domain);
  useEffect(() => {
    if (!focusDomain) return;
    if (names.includes(focusDomain) && focusDomain !== '∅') {
      wrap.current?.querySelector<HTMLElement>(`[data-domain="${focusDomain}"] .gp-cb-act button`)?.focus();
      setFocusDomain(null);
    } else if (focusDomain === '∅' && !names.length) { done.current?.focus(); setFocusDomain(null); }
  });
  const decided = async (domain: string, msg: string) => {
    const next = nextFocusId(names, [domain]);
    await onDone(domain, msg);
    setFocusDomain(next ?? '∅');
  };
  return (
    <div className="gp-inv-panel" id="inv-panel-domains" ref={wrap}>
      <div className="gp-cb-head">
        <h4 className="gp-card-h" ref={h} tabIndex={-1}>{list.length ? `Outbound domains to decide · ${list.length}` : 'All domains decided'}</h4>
        <Button ref={done} compact variant={list.length ? 'ghost' : 'primary'} onClick={onClose}>Done</Button>
      </div>
      {list.length ? <ul className="gp-cb-rows">{list.map((d) => <DomainRow key={d.domain} slug={slug} domain={d.domain} hits={d.hits} onDone={decided} onIntake={onIntake} />)}</ul> : null}
    </div>
  );
}

function DomainRow({ slug, domain, hits, onDone, onIntake }: { slug: string; domain: string; hits: number; onDone: (domain: string, msg: string) => Promise<void>; onIntake: () => void }) {
  const [mode, setMode] = useState<null | 'service' | 'ignore'>(null);
  const [refocus, setRefocus] = useState<null | 'service' | 'ignore'>(null);
  const [q, setQ] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const li = useRef<HTMLLIElement>(null);
  const choices = useMemo(() => productChoices(), []);
  const matches = filterByName(choices, q);
  const said = useSettled(q.trim() ? (matches.length ? `${matches.length} service${matches.length === 1 ? '' : 's'} match` : `No service matches “${q.trim()}”`) : '');
  useEffect(() => {
    if (!refocus || mode) return;
    li.current?.querySelector<HTMLElement>(`[data-opens="${refocus}"]`)?.focus();
    setRefocus(null);
  }, [refocus, mode]);
  const cancel = () => { setRefocus(mode); setMode(null); };
  const escCancel = (e: React.KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); cancel(); } };
  const run = async (b: Parameters<typeof resolveEgress>[1], msg: string) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try { await resolveEgress(slug, b); await onDone(domain, msg); } catch (e) { setErr(`${domain}: ${errMsg(e, 'Could not save.')}`); setBusy(false); }
  };
  return (
    <li className="gp-cb-row" data-domain={domain} ref={li}>
      <span className="gp-cb-main">
        <b>{domain}</b>
        <span className="gp-cb-meta">{`${hits} request${hits === 1 ? '' : 's'} seen`}</span>
        {err ? <Alert variant="error">{err}</Alert> : null}
        {mode === 'service' ? (
          <div className="gp-inv-link" role="group" aria-label={`Which service is ${domain}?`} data-esc-owner="" onKeyDown={escCancel}>
            <Field id={`dom-svc-${domain}`} label="Which service is it?" hint={q.trim() ? `${matches.length} found` : 'Type part of its name'}>
              <Input autoFocus value={q} onChange={(e) => setQ(e.currentTarget.value)} />
            </Field>
            <p className="sr-only" role="status" aria-live="polite">{said}</p>
            {q.trim() && !matches.length ? (
              <p className="gp-inv-nomatch">No match. <button type="button" className="gp-cb-textbtn" onClick={onIntake}>Name it in the questions instead</button></p>
            ) : (
              <ul className="gp-inv-picks">
                {matches.slice(0, q.trim() ? matches.length : 0).map((c) => (
                  <li key={c.ref}><button type="button" className="gp-inv-pick" onClick={() => void run({ domain, as: 'service', ref: c.ref }, `${domain} is ${c.name} · it's in To review`)}>{c.name}</button></li>
                ))}
              </ul>
            )}
            <Button compact variant="ghost" onClick={cancel}>Cancel</Button>
          </div>
        ) : null}
        {mode === 'ignore' ? (
          <form className="gp-inline-form" data-esc-owner="" onKeyDown={escCancel} onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 3) void run({ domain, as: 'ignore', reason: reason.trim() }, `Ignored ${domain}`); }}>
            <Field id={`dom-why-${domain}`} label="Why it is not a third party" hint="At least 3 characters.">
              <Input autoFocus value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
            </Field>
            <div className="gp-actions">
              <Button type="submit" compact variant="primary" aria-disabled={busy || reason.trim().length < 3 || undefined} aria-describedby={`dom-why-${domain}-hint`}>{busy ? 'Saving…' : 'Ignore'}</Button>
              <Button compact variant="ghost" onClick={cancel}>Cancel</Button>
            </div>
          </form>
        ) : null}
      </span>
      {mode === null ? (
        <span className="gp-cb-act">
          <Button compact aria-disabled={busy || undefined} onClick={() => void run({ domain, as: 'first_party' }, `${domain} marked as yours`)} aria-label={`This is us · ${domain}`}>This is us</Button>
          <Button compact variant="ghost" data-opens="service" onClick={() => setMode('service')} aria-label={`It's a service · ${domain}`}>It's a service…</Button>
          <Button compact variant="ghost" data-opens="ignore" onClick={() => setMode('ignore')} aria-label={`Ignore · ${domain}`}>Ignore…</Button>
        </span>
      ) : null}
    </li>
  );
}

/**
 * A scan problem: the reason in plain words and the action that fixes it. A lost status has no
 * Dismiss (Check again is the way out); a connection or setup problem also offers Scan again for
 * after the fix.
 */
function ScanProblemBanner({ slug, prefix, canWrite, askWhat, problem, onDismiss, onScanAgain, onCheckAgain }: {
  slug: string; prefix: string;
  /** Read-only members: the reason in plain words and Ask an admin; never Scan again or the GitHub link. */
  canWrite: boolean; askWhat: string;
  problem: ScanProblem; onDismiss: () => void; onScanAgain: () => void; onCheckAgain: () => void;
}) {
  const code = problem.kind === 'failed' ? problem.job.error_code ?? 'scan_failed' : null;
  const message = problem.kind === 'start' ? problem.message
    : problem.kind === 'lost' ? `${problem.job.target}: we can't reach the scan status right now.`
      : problem.kind === 'gone' ? `${problem.job.target}: the scan could not be found.`
        : `${problem.job.target}: ${problem.job.error ?? 'The scan stopped before it finished.'}`;
  if (!canWrite && problem.kind !== 'lost') {
    return (
      <Alert variant="info" onDismiss={onDismiss}>
        <p>{message}</p>
        <AskAdmin slug={slug} compact verb="scan the code again" what={askWhat} subject="GAC Pro: scan the code again" />
      </Alert>
    );
  }
  const again = <Button compact variant="ghost" onClick={onScanAgain}>Scan again</Button>;
  const action = problem.kind === 'lost' ? <Button compact onClick={onCheckAgain}>Check again</Button>
    : code === 'no_credential' && 'job' in problem ? <span className="gp-actions"><ReconnectGitHub slug={slug} connectorId={problem.job.connector_id} target={problem.job.target} compact announce={false} />{again}</span>
    : code && SCAN_CONNECTION.has(code) ? <span className="gp-actions"><Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/settings/integrations`}>Open GitHub connection</Link>{again}</span>
      : code === 'not_configured' ? <span className="gp-actions"><AskAdmin slug={slug} compact verb="turn on repository scanning" what="runs GAC Pro for this workspace" subject="GAC Pro: turn on repository scanning" />{again}</span>
        : <Button compact onClick={onScanAgain}>Scan again</Button>;
  return <Alert variant="error" onDismiss={problem.kind === 'lost' ? undefined : onDismiss}><p>{message}</p>{action}</Alert>;
}

function ScanPanel({ slug, prefix, jobs, initialId, lost, rows, returned, problem, askWhat, canWrite, onDismissProblem, onCheckAgain, onClose, onStarted, onStartFailed }: {
  /** Back from a GitHub Reconnect: the outcome to show at the top of the panel. */
  returned?: { ok: boolean; text: string } | null;
  slug: string; prefix: string;
  /** Per connection: its running scan, or the last one that failed. */
  jobs: Record<string, ScanJob>;
  /** The connection to show first (Scan again from the page banner opens on the one that failed). */
  initialId: string | null;
  /** The status could not be read three times in a row: progress is not shown, Check again is. */
  lost: boolean;
  /** The progress rows (with Scan again on a stalled one), ids `inv-scan-line-<connector>`. */
  rows: (jobs: ScanJob[]) => React.ReactNode;
  /** A scan problem the panel itself doesn't show inline (another connection, a lost status, a vanished job). */
  problem: ScanProblem | null;
  askWhat: string;
  /** Only writers reach the panel today; passed through so its banner never offers what a reader can't do. */
  canWrite: boolean;
  /** Clears the problem and says where focus goes next (the banner that held it unmounts). */
  onDismissProblem: (focusNext: 'scan-submit' | 'scan-heading') => void;
  onCheckAgain: () => void;
  onClose: () => void;
  onStarted: (job: ScanJob, reply?: { outcome?: ScanStartOutcome; queued_mode?: 'full' }) => void;
  /** The start request failed: the parent names it when the panel was closed meanwhile. */
  onStartFailed: (message: string) => void;
}) {
  const conns = useQuery({ queryKey: ['infra-plan', slug, 'github-connections'], queryFn: () => listConnectors(slug) });
  const scans = useQuery({ queryKey: ['infra-plan', slug, 'scans'], queryFn: () => listScans(slug) });
  // An expired sign-in stays listed (with Reconnect) instead of the panel claiming GitHub isn't connected.
  const gh = (conns.data?.connectors ?? []).filter((c) => c.kind === 'github' && (c.status === 'active' || c.needs_reconnect));
  const [id, setId] = useState(initialId ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  /** The start said the sign-in expired (no_credential): show Reconnect at once, not a generic error first. */
  const [expiredNow, setExpiredNow] = useState<string | null>(null);
  const h = usePanelFocus();
  const firstRunning = Object.values(jobs).find(scanActive);
  const chosen = id || firstRunning?.connector_id || gh[0]?.connector_id || '';
  const mine = jobs[chosen];
  const running = scanActive(mine) ? mine : null;
  const failed = mine && mine.status === 'failed' ? mine : null;
  const expired = !!gh.find((c) => c.connector_id === chosen)?.needs_reconnect || expiredNow === chosen;
  // While the status is lost, Scan stays usable: the server rejoins the running job (or replaces a stalled one).
  const busy = starting || (!!running && !lost);
  const shownInline = problem?.kind === 'failed' && problem.job.connector_id === chosen;
  const allRunning = Object.values(jobs).filter(scanActive);
  const configRepo = (connectorId: string) => {
    const cfg = gh.find((c) => c.connector_id === connectorId)?.config_json;
    try { const v = cfg ? JSON.parse(cfg) as { owner?: string; repo?: string } : {}; return v.owner && v.repo ? `${v.owner}/${v.repo}` : null; } catch { return null; }
  };
  const repoOf = (connectorId: string) => scans.data?.scans.find((s) => s.connector_id === connectorId)?.target ?? jobs[connectorId]?.target ?? configRepo(connectorId);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (!chosen || busy || expired) return; setStarting(true); setErr(null);
    try { const r = await startScan(slug, chosen); onStarted(r.scan, { outcome: r.outcome, queued_mode: r.queued_mode }); } catch (x) {
      const msg = errMsg(x, 'The repository could not be scanned.');
      if (x instanceof ApiError && (x.body as { code?: string } | undefined)?.code === 'no_credential') { setExpiredNow(chosen); void conns.refetch(); }
      else setErr(msg);
      onStartFailed(msg);
    } finally { setStarting(false); }
  };
  const failedCode = failed?.error_code ?? null;
  return (
    <div className="gp-inv-panel" id="inv-panel-scan">
      <div className="gp-cb-head"><h4 className="gp-card-h" ref={h} tabIndex={-1}>Scan code</h4></div>
      {conns.isLoading ? <Spinner label="Loading connections" /> : gh.length === 0 ? (
        <EmptyState title="Connect GitHub to scan your code" action={<span className="gp-actions">
          <Link className="gp-linkbtn" data-variant="primary" to={`${prefix}/setup`}>Connect GitHub</Link>
          <Button compact variant="ghost" onClick={onClose}>Close</Button>
        </span>} />
      ) : (
        <form className="gp-inline-form" onSubmit={submit} aria-label="Scan code">
          {problem && !shownInline ? (
            <ScanProblemBanner slug={slug} prefix={prefix} canWrite={canWrite} askWhat={askWhat} problem={problem} onDismiss={() => onDismissProblem('scan-heading')} onCheckAgain={onCheckAgain}
              onScanAgain={() => { if ('job' in problem) setId(problem.job.connector_id); onDismissProblem('scan-submit'); }} />
          ) : null}
          {returned?.ok ? <Alert variant="success"><p>{returned.text}</p></Alert> : null}
          {expired ? (
            <Alert variant="warning" id="inv-scan-expired"><p>{returned && !returned.ok ? `${returned.text} ` : ''}{`${repoOf(chosen) ?? 'GitHub'}: sign-in expired.`}</p>
              <ReconnectGitHub slug={slug} connectorId={chosen} target={repoOf(chosen)} compact announce={false} /></Alert>
          ) : err ? <Alert variant="error">{err}</Alert> : returned && !returned.ok ? <Alert variant="error"><p>{returned.text}</p></Alert> : null}
          {failed && !err && !expired ? (
            <Alert variant="error">
              <p>{failed.error ?? 'The scan stopped before it finished.'}</p>
              {failedCode === 'no_credential' ? <ReconnectGitHub slug={slug} connectorId={failed.connector_id} target={failed.target} compact announce={false} />
                : failedCode && SCAN_CONNECTION.has(failedCode) ? <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/settings/integrations`}>Open GitHub connection</Link> : null}
              {failedCode === 'not_configured' ? <AskAdmin slug={slug} compact verb="turn on repository scanning" what="runs GAC Pro for this workspace" subject="GAC Pro: turn on repository scanning" /> : null}
            </Alert>
          ) : null}
          <Field id="inv-scan-conn" label="Repository" hint="Reads manifests, config and imports. Tests and build output are skipped.">
            <Select value={chosen} onChange={(e) => { setId(e.currentTarget.value); setErr(null); }}>
              {gh.map((c) => <option key={c.connector_id} value={c.connector_id}>{`${repoOf(c.connector_id) ?? `GitHub · connected ${fmtDate(c.configured_at)}`}${c.needs_reconnect ? ' · sign-in expired' : ''}`}</option>)}
            </Select>
          </Field>
          {lost ? null : rows(allRunning)}
          <div className="gp-actions">
            {/* While the sign-in is expired, Reconnect is the panel's one filled button; Scan waits for it. */}
            <Button type="submit" compact variant={expired ? 'secondary' : 'primary'} aria-busy={starting || undefined} aria-disabled={busy || expired || undefined}
              aria-describedby={expired ? 'inv-scan-expired' : running && !lost ? `inv-scan-line-${running.connector_id}` : undefined}>{failed && failedCode && SCAN_RETRYABLE.has(failedCode) ? 'Scan again' : 'Scan'}</Button>
            <Button compact variant="ghost" onClick={onClose}>{allRunning.length ? 'Close · we’ll add what we find' : 'Cancel'}</Button>
          </div>
          {/* Announced once per scan (the file count on screen is not re-announced). */}
          <p className="sr-only" role="status" aria-live="polite">{running ? `Scanning ${running.target}` : starting ? 'Starting the scan' : ''}</p>
        </form>
      )}
    </div>
  );
}

function EgressPanel({ slug, onClose, onDone }: { slug: string; onClose: () => void; onDone: (t: { message: string; details: string }) => Promise<void> }) {
  const [source, setSource] = useState<EgressSourceKey>('cloudflare_logpush');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const h = usePanelFocus();
  const onFile = (f: File | undefined) => {
    setErr(null);
    if (!f) return;
    if (f.size > MAX_UPLOAD) { setErr(`${f.name} is ${fileSizeLabel(f.size)}; the limit is 5 MB. Upload a shorter window of the log.`); return; }
    setFile(f);
  };
  const ready = !!file || !!text.trim();
  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (!ready || busy) return; setBusy(true); setErr(null);
    try {
      const log = file ? await file.text() : text;
      const r = await uploadEgress(slug, source, log);
      const x = r.egress;
      const read = `${x.parsed.lines} line${x.parsed.lines === 1 ? '' : 's'} read${x.parsed.skipped ? `, ${x.parsed.skipped} skipped` : ''}${x.parsed.truncated ? ', the rest cut at the limit' : ''}`;
      const from = file ? file.name : 'the pasted lines';
      await onDone({ message: `${x.suggested + x.unknown} to review from ${from}`, details: `${x.domains} domain${x.domains === 1 ? '' : 's'} · ${x.matched} known · ${x.suggested + x.unknown} to review · ${read}` });
    } catch (x) { setErr(errMsg(x, 'The log could not be read.')); } finally { setBusy(false); }
  };
  return (
    <form className="gp-inline-form gp-inv-panel" id="inv-panel-egress" onSubmit={submit} aria-labelledby="inv-egress-h">
      <div className="gp-cb-head"><h4 className="gp-card-h" id="inv-egress-h" ref={h} tabIndex={-1}>Upload a network log</h4></div>
      {err ? <Alert variant="error">{err}</Alert> : null}
      <Field id="inv-egress-src" label="Log type">
        <Select value={source} onChange={(e) => setSource(e.currentTarget.value as EgressSourceKey)}>
          {EGRESS_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
      </Field>
      {file ? (
        <p className="gp-inv-file"><b>{file.name}</b> <span className="gp-dim">{fileSizeLabel(file.size)}</span> <button type="button" className="gp-cb-textbtn" onClick={() => setFile(null)}>Remove</button></p>
      ) : (
        <>
          <Field id="inv-egress-file" label="Log file" hint="Up to 5 MB. Only domain names are kept.">
            <Input type="file" accept=".json,.ndjson,.log,.txt" onChange={(e) => onFile(e.currentTarget.files?.[0])} />
          </Field>
          <Field id="inv-egress-text" label="Or paste lines">
            <Textarea rows={4} value={text} onChange={(e) => setText(e.currentTarget.value)} />
          </Field>
        </>
      )}
      <div className="gp-actions">
        <Button type="submit" compact variant="primary" aria-busy={busy || undefined} aria-disabled={busy || !ready || undefined}>{busy ? 'Reading…' : 'Upload'}</Button>
        <Button compact variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}

function IntakePanel({ slug, onClose, onSaved }: { slug: string; onClose: () => void; onSaved: (question: string) => Promise<void> }) {
  const intake = useQuery({ queryKey: ['infra-plan', slug, 'intake'], queryFn: () => getIntake(slug) });
  const h = usePanelFocus();
  const qs = intake.data?.intake.questions ?? [];
  const answered = qs.filter((q) => q.answers.length > 0);
  const next = qs.filter((q) => q.answers.length === 0);
  return (
    <div className="gp-inv-panel" id="inv-panel-intake">
      <div className="gp-cb-head">
        <h4 className="gp-card-h" ref={h} tabIndex={-1}>{`Things no scan can see${qs.length ? ` · ${answered.length} of ${qs.length} answered` : ''}`}</h4>
        <Button compact variant="ghost" onClick={onClose}>Done</Button>
      </div>
      {intake.isLoading ? <Spinner label="Loading the questions" /> : !intake.data ? (
        <Alert variant="error"><p>The questions could not load.</p><Button compact onClick={() => void intake.refetch()}>Retry</Button></Alert>
      ) : (
        <>
          {next[0] ? <IntakeQuestion key={next[0].key} slug={slug} q={next[0]} onSaved={async () => { await intake.refetch(); await onSaved(next[0]!.text); }} /> : <p>Every question is answered.</p>}
          {answered.map((q) => (
            <details key={q.key} className="gp-cb-why">
              <summary>{`${q.text} · Answered`}</summary>
              <IntakeQuestion slug={slug} q={q} onSaved={async () => { await intake.refetch(); await onSaved(q.text); }} />
            </details>
          ))}
        </>
      )}
    </div>
  );
}

function IntakeQuestion({ slug, q, onSaved }: {
  slug: string;
  q: { key: string; text: string; options: readonly string[]; option_names: Record<string, string>; answers: Array<{ choice: string; provider_key: string | null; product: string | null; other_text: string | null }> };
  onSaved: () => Promise<void>;
}) {
  const current = useMemo(() => q.answers.filter((a) => a.choice === 'product' && a.provider_key && a.product).map((a) => `${a.provider_key}/${a.product}`), [q.answers]);
  const [picked, setPicked] = useState<string[]>(current);
  const [none, setNone] = useState(q.answers.some((a) => a.choice === 'none'));
  const [other, setOther] = useState(q.answers.find((a) => a.choice === 'other')?.other_text ?? '');
  const [describing, setDescribing] = useState(false);
  const [describe, setDescribe] = useState('');
  const [proposals, setProposals] = useState<ElicitProposal[]>([]);
  const [found, setFound] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [finding, setFinding] = useState(false);
  const names: Record<string, string> = { ...q.option_names };
  for (const p of proposals) names[p.value] ??= p.quoted_span ?? p.value;
  const choices = [...new Set([...q.options, ...current, ...proposals.map((p) => p.value)])];
  const toggle = (ref: string, on: boolean) => { setNone(false); setPicked((xs) => (on ? [...new Set([...xs, ref])] : xs.filter((x) => x !== ref))); };
  const find = async () => {
    if (finding || !describe.trim()) return;
    setErr(null); setFinding(true); setFound('');
    try {
      const ps = (await elicitIntake(slug, q.key, describe)).proposals;
      setProposals(ps);
      if (ps.length) { setNone(false); setPicked((xs) => [...new Set([...xs, ...ps.map((p) => p.value)])]); }
      setFound(ps.length ? `${ps.length} found, ticked below. Save to keep them.` : 'No services named in that text. Pick them from the list or add one.');
    } catch (x) { setErr(errMsg(x, 'The text could not be read.')); } finally { setFinding(false); }
  };
  const empty = !none && !picked.length && !other.trim();
  const save = async (e: FormEvent) => {
    e.preventDefault(); if (busy || empty) return; setBusy(true); setErr(null);
    try {
      const spans = Object.fromEntries(proposals.filter((p) => picked.includes(p.value) && p.quoted_span).map((p) => [p.value, p.quoted_span!]));
      await submitIntake(slug, { question_key: q.key, ...(none ? { none: true } : { refs: picked, other: other.trim() ? [other.trim()] : [], quoted_spans: spans }) });
      await onSaved();
    } catch (x) { setErr(errMsg(x, 'The answer could not be saved.')); } finally { setBusy(false); }
  };
  return (
    <form className="gp-inv-q" onSubmit={save} aria-labelledby={`iq-${q.key}`}>
      <fieldset className="gp-cb-fieldset">
        <legend id={`iq-${q.key}`}>{q.text}</legend>
        {err ? <Alert variant="error">{err}</Alert> : null}
        {choices.map((ref) => <Checkbox key={ref} id={`iq-${q.key}-${ref}`} label={names[ref] ?? 'A service'} checked={picked.includes(ref)} onChange={(e) => toggle(ref, e.currentTarget.checked)} />)}
        <Checkbox id={`iq-${q.key}-none`} label="None of these, and nothing else" checked={none} onChange={(e) => { setNone(e.currentTarget.checked); if (e.currentTarget.checked) { setPicked([]); setOther(''); } }} />
        <Field id={`iq-${q.key}-other`} label="Something else">
          <Input value={other} onChange={(e) => { setOther(e.currentTarget.value); setNone(false); }} />
        </Field>
        {describing ? (
          <>
            <Field id={`iq-${q.key}-describe`} label="Describe it in your words" hint="We list only services you name.">
              <Textarea rows={2} value={describe} onChange={(e) => setDescribe(e.currentTarget.value)} />
            </Field>
            <div className="gp-actions"><Button compact variant="ghost" aria-busy={finding || undefined} aria-disabled={finding || !describe.trim() || undefined} onClick={() => void find()}>{finding ? 'Finding…' : 'Find services in my text'}</Button></div>
          </>
        ) : <button type="button" className="gp-cb-textbtn" onClick={() => setDescribing(true)}>Not listed? Describe it</button>}
        <p className="sr-only" role="status" aria-live="polite">{found}</p>
        {found ? <p className="gp-dim">{found}</p> : null}
        <div className="gp-actions">
          <Button type="submit" compact variant="primary" aria-disabled={busy || empty || undefined}>{busy ? 'Saving…' : 'Save answer'}</Button>
        </div>
      </fieldset>
    </form>
  );
}

